import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Button,
  Dialog,
  EmptyState,
  FormField,
  KycStatusBadge,
  Select,
  Skeleton,
  Textarea,
} from "@chotca/ui";
import type { ChangeRejectCode } from "@chotca/config";
import {
  useAdminIdentity,
  useApproveIdentity,
  useRejectIdentity,
} from "@chotca/api-client";

// Standardized reject codes with admin-facing labels (spec §7.4). OTHER requires
// a free-text detail; the rest map to a fixed fix-it hint shown to the user.
const REJECT_CODES: { value: ChangeRejectCode; label: string }[] = [
  { value: "DOC_UNREADABLE", label: "Ảnh giấy tờ không đọc được" },
  { value: "DOC_EXPIRED", label: "Giấy tờ đã hết hạn" },
  { value: "NAME_MISMATCH", label: "Họ tên không khớp" },
  { value: "WRONG_DOC_TYPE", label: "Sai loại giấy tờ" },
  { value: "OTHER", label: "Lý do khác (nhập chi tiết)" },
];

export function IdentityDetailClient({ id }: { id: string }) {
  const nav = useNavigate();
  const { data, isLoading, isError, refetch } = useAdminIdentity(id);
  const approve = useApproveIdentity();
  const reject = useRejectIdentity();

  const [rejectOpen, setRejectOpen] = useState(false);
  const [code, setCode] = useState<ChangeRejectCode>("DOC_UNREADABLE");
  const [detail, setDetail] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 rounded-md" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="aspect-[1/1.585] w-full rounded-md" />
          <Skeleton className="aspect-[1/1.585] w-full rounded-md" />
        </div>
        <Skeleton className="h-40 w-full rounded-md" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <EmptyState
        title="Không tải được hồ sơ"
        action={<Button onClick={() => refetch()}>Thử lại</Button>}
      />
    );
  }

  const status = data.status;
  const isPending = status === "PENDING";
  const busy = approve.isPending || reject.isPending;

  const handleApprove = async () => {
    setError(null);
    try {
      await approve.mutateAsync(id);
      nav("/kyc/identity");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Duyệt thất bại");
    }
  };

  const handleReject = async () => {
    setError(null);
    if (code === "OTHER" && !detail.trim()) {
      setError("Vui lòng nhập chi tiết cho lý do khác.");
      return;
    }
    try {
      await reject.mutateAsync({
        userId: id,
        body: { code, detail: detail.trim() || null },
      });
      setRejectOpen(false);
      nav("/kyc/identity");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Từ chối thất bại");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{data.fullName}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-ink/60">
            <KycStatusBadge status={status} />
            <span>
              · Nộp {new Date(data.submittedAt).toLocaleString("vi-VN")}
            </span>
          </div>
        </div>
      </header>

      <section>
        <h2 className="mb-3 text-sm uppercase text-ink/60">Ảnh CCCD</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <PhotoTile label="Mặt trước CCCD" url={data.idFrontSignedUrl} />
          <PhotoTile label="Mặt sau CCCD" url={data.idBackSignedUrl} />
        </div>
        <p className="mt-2 text-xs text-ink/50">
          URL ảnh hết hạn lúc{" "}
          {new Date(data.signedUrlExpiresAt).toLocaleTimeString("vi-VN")}.
        </p>
      </section>

      <section className="rounded-md border border-ink/10 bg-paper p-5">
        <h2 className="mb-4 text-sm uppercase text-ink/60">
          Thông tin khai báo
        </h2>
        <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Họ và tên" value={data.fullName} />
          <Field
            label="Số CCCD"
            value={data.idNumber || "(không giải mã được)"}
            mono
          />
          <Field label="Ngày sinh" value={data.dob ?? "—"} />
          <Field label="Phone" value={data.userPhone ?? "—"} mono />
          <Field label="Email" value={data.userEmail ?? "—"} />
          <Field
            label="Duyệt lúc"
            value={
              data.reviewedAt
                ? new Date(data.reviewedAt).toLocaleString("vi-VN")
                : "—"
            }
          />
        </dl>
      </section>

      {data.rejectCode ? (
        <section className="rounded-md border-l-4 border-ember bg-ember/5 p-4">
          <p className="text-xs font-bold uppercase text-ember">
            Lý do từ chối lần trước
          </p>
          <p className="mt-1 text-sm text-ink">
            {REJECT_CODES.find((c) => c.value === data.rejectCode)?.label ??
              data.rejectCode}
            {data.rejectDetail ? ` — ${data.rejectDetail}` : ""}
          </p>
        </section>
      ) : null}

      {error ? (
        <p className="rounded-md bg-ember/10 px-3 py-2 text-sm text-ember">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-end gap-3 border-t border-ink/10 pt-4">
        <Button
          variant="ghost"
          onClick={() => {
            setCode("DOC_UNREADABLE");
            setDetail("");
            setError(null);
            setRejectOpen(true);
          }}
          disabled={!isPending || busy}
        >
          Từ chối
        </Button>
        <Button
          variant="secondary"
          onClick={handleApprove}
          disabled={!isPending || busy}
          loading={approve.isPending}
        >
          Duyệt
        </Button>
      </footer>

      <Dialog
        open={rejectOpen}
        onClose={() => {
          if (!reject.isPending) setRejectOpen(false);
        }}
        title="Từ chối hồ sơ định danh"
        description="Chọn mã lý do chuẩn hoá. Người dùng sẽ thấy hướng dẫn khắc phục tương ứng."
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setRejectOpen(false)}
              disabled={reject.isPending}
            >
              Huỷ
            </Button>
            <Button
              variant="urgent"
              onClick={handleReject}
              loading={reject.isPending}
            >
              Xác nhận từ chối
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <FormField label="Mã lý do">
            <Select
              value={code}
              onValueChange={(v) => setCode(v as ChangeRejectCode)}
              options={REJECT_CODES}
            />
          </FormField>
          {code === "OTHER" ? (
            <FormField label="Chi tiết">
              <Textarea
                rows={3}
                maxLength={500}
                placeholder="Mô tả lý do cụ thể…"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />
            </FormField>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
}

function PhotoTile({
  label,
  url,
}: {
  label: string;
  url: string | null | undefined;
}) {
  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden rounded-md border border-ink/10 bg-mist"
    >
      {url ? (
        <img
          src={url}
          alt={label}
          className="aspect-[1/1.585] w-full object-cover"
        />
      ) : (
        <div className="flex aspect-square items-center justify-center text-sm text-ink/40">
          (no url)
        </div>
      )}
      <div className="border-t border-ink/10 bg-paper px-3 py-2 text-xs font-medium text-ink/70">
        {label} · mở ảnh đầy đủ
      </div>
    </a>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase text-ink/50">{label}</dt>
      <dd className={mono ? "mt-0.5 font-mono text-sm" : "mt-0.5 text-sm"}>
        {value}
      </dd>
    </div>
  );
}
